// Multi-child parent console — per-child data isolation.
//
// Storage model:
//   mwq_children             — JSON array of child profile objects.
//   mwq_active_child_id      — string id of the currently-playing child.
//   we_child__<id>__<key>    — bucketed copy of every "scoped" key for a
//                              non-active child. When we switch children
//                              we stash the current live keys into the
//                              outgoing child's bucket and hydrate the
//                              live keys from the incoming child's bucket.
//                              Everything downstream continues to read
//                              the plain keys and Just Works.
//
// Design principle: keep the surface area tiny so no existing library
// needs to be aware of children. Only `Family.jsx` and the header
// switcher import from here.

const CHILDREN_KEY = "mwq_children";
const ACTIVE_ID_KEY = "mwq_active_child_id";

// Legacy key kept for backward-compat with the older Family.jsx that
// stored the FULL child object here. We migrate on first read.
const LEGACY_ACTIVE_OBJ_KEY = "mwq_active_child";

// Every localStorage key that represents child-specific state. We stash
// and hydrate exactly this set when swapping the active child.
const SCOPED_KEYS = [
  "we_progress_v1",
  "we_kid_name",
  "we_birth_country_code",
  "we_heritage_codes",
  "we_born_in_seen",
  "we_first_visit_day",
  "we_age_group",
  "we_wonder_passport_v1",
  "we_standards_practised_v1",
  "mwq_daily_today",
  "mwq_daily_recap",
  "mwq_daily_streak",
  // Build 1 · canonical skill ledger (per-child from Jun 2026)
  "we_skill_ledger_v1",
  "we_skill_events_v1",
  "we_skill_ledger_version",
];

const TIER_TO_AGE = { seedlings: "little", explorers: "big", pioneers: "older" };

const safeGet = (k) => { try { return localStorage.getItem(k); } catch { return null; } };
const safeSet = (k, v) => { try { localStorage.setItem(k, v); } catch { /* ignore quota */ } };
const safeRm  = (k)    => { try { localStorage.removeItem(k); } catch { /* ignore */ } };

const emit = (name, detail) => {
  try { window.dispatchEvent(new CustomEvent(name, { detail })); } catch { /* SSR / tests */ }
};

const uid = () => {
  try { return crypto.randomUUID?.() || String(Date.now()) + Math.random().toString(36).slice(2, 8); }
  catch { return String(Date.now()) + Math.random().toString(36).slice(2, 8); }
};

/** Read the raw children array. Never throws.
 *  Feb 2026 · Self-heal: any legacy child written before Part 7 lacks
 *  `curriculum` + `subjects`. This is a persistent bug source (Bug #9
 *  — Weekly plan reads "0 subjects"). Backfill sensible defaults on
 *  read + persist so every downstream consumer sees a valid child.
 */
export function listChildren() {
  let list;
  try { list = JSON.parse(safeGet(CHILDREN_KEY) || "[]"); }
  catch { return []; }
  if (!Array.isArray(list)) return [];
  let dirty = false;
  const healed = list.map((c) => {
    if (!c || typeof c !== "object") return c;
    const patch = {};
    if (!c.curriculum) patch.curriculum = "uk-ks2";
    if (!Array.isArray(c.subjects) || c.subjects.length === 0) {
      patch.subjects = ["maths", "english", "science"];
    }
    if (Object.keys(patch).length > 0) {
      dirty = true;
      return { ...c, ...patch };
    }
    return c;
  });
  if (dirty) {
    try { safeSet(CHILDREN_KEY, JSON.stringify(healed)); } catch { /* ignore quota */ }
  }
  return healed;
}

function saveList(list) {
  safeSet(CHILDREN_KEY, JSON.stringify(list));
  emit("we-children-changed", { count: list.length });
}

/** Return the currently-active child id (string) or null. */
export function getActiveChildId() {
  const id = safeGet(ACTIVE_ID_KEY);
  if (id) return id;
  // Legacy migration: older Family.jsx stored the full object here.
  try {
    const obj = JSON.parse(safeGet(LEGACY_ACTIVE_OBJ_KEY) || "null");
    if (obj && obj.id) {
      safeSet(ACTIVE_ID_KEY, obj.id);
      return obj.id;
    }
  } catch { /* ignore */ }
  return null;
}

/** Convenience: full active-child object, or null. */
export function getActiveChild() {
  const id = getActiveChildId();
  if (!id) return null;
  return listChildren().find((c) => c.id === id) || null;
}

const bucketKey = (childId, key) => `we_child__${childId}__${key}`;

/** Copy every SCOPED_KEYS value out of the live keys into the child's bucket. */
function stashActive(childId) {
  if (!childId) return;
  for (const k of SCOPED_KEYS) {
    const v = safeGet(k);
    if (v == null) safeRm(bucketKey(childId, k));
    else safeSet(bucketKey(childId, k), v);
  }
}

/** Copy the child's bucketed values into the live keys. */
function hydrateActive(childId) {
  if (!childId) return;
  for (const k of SCOPED_KEYS) {
    const v = safeGet(bucketKey(childId, k));
    if (v == null) safeRm(k);
    else safeSet(k, v);
  }
}

function syncLiveConvenienceKeys(child) {
  if (!child) return;
  if (child.name) safeSet("we_kid_name", child.name);
  if (child.birthCountry) safeSet("we_birth_country_code", child.birthCountry);
  if (child.tier && TIER_TO_AGE[child.tier]) {
    safeSet("we_age_group", TIER_TO_AGE[child.tier]);
    safeSet("we_age_seen", "1");
  }
}

/**
 * Add a new child. If it's the very first child, we treat the current
 * live localStorage state as their starter progress (auto-migration
 * from single-child mode) — no data loss for existing users.
 */
export function addChild(profile) {
  const list = listChildren();
  // Feb 2026 · Zero-subject guard — a child with a curriculum but no
  // subjects has nothing to learn. Every add-child path now defaults
  // to the core three (maths, English, science) so the plan always
  // has content. Callers may override with an explicit subjects list;
  // an empty/invalid list falls back to the core default.
  const rawSubjects = Array.isArray(profile.subjects) ? profile.subjects.filter(Boolean) : [];
  const subjects = rawSubjects.length > 0 ? rawSubjects : ["maths", "english", "science"];
  const child = {
    id: uid(),
    createdAt: new Date().toISOString(),
    name: String(profile.name || "").trim().slice(0, 40) || "Explorer",
    tier: profile.tier || "explorers",
    // Cycle 62 · School year (1..10) — collected in the Family wizard
    // so lesson filters, Maths Academy and Learn Topics land on the
    // child's exact year instead of the whole tier band. Nullable so
    // legacy children created before this cycle keep working (they
    // fall back to their tier's default year at read time).
    year: (typeof profile.year === "number" && profile.year >= 1 && profile.year <= 10) ? profile.year : null,
    birthCountry: profile.birthCountry || "",
    avatar: profile.avatar || "🦊",
    curriculum: profile.curriculum || "uk-ks2",
    subjects,
    // Cycle 1 · Leveled Reading — reading_level is SEPARATE from the
    // phonics stage and SEPARATE from the audio-support level. Three
    // distinct dials; never conflated. Null = "not placed yet" so the
    // Reading Session offers the placement flow on first visit.
    readingLevel: null,
    audioSupportLevel: 2,          // 1=silent · 2=on-tap · 3=default-on
    readingHistory: [],            // {passageId, level, score, ts, category:{...}}
  };
  list.push(child);
  saveList(list);
  const isFirst = list.length === 1;
  if (isFirst) {
    // Adopt whatever's currently in localStorage as this child's data.
    stashActive(child.id);
    safeSet(ACTIVE_ID_KEY, child.id);
    syncLiveConvenienceKeys(child);
  }
  emit("we-children-changed", { count: list.length });
  return child;
}

/** Patch an existing child's profile. */
export function updateChild(childId, patch) {
  // Feb 2026 · Zero-subject guard — never let a curriculum-tagged
  // child drop to `subjects: []`. If a caller explicitly passes an
  // empty array or a non-array, we drop the field from the patch so
  // the previous subjects survive. To CLEAR subjects intentionally,
  // callers must set subjects=null (which we also block for safety).
  const safePatch = { ...patch };
  if ("subjects" in safePatch) {
    const s = safePatch.subjects;
    if (!Array.isArray(s) || s.length === 0) {
      delete safePatch.subjects;
    }
  }
  const list = listChildren().map((c) => (c.id === childId ? { ...c, ...safePatch } : c));
  saveList(list);
  if (getActiveChildId() === childId) {
    const child = list.find((c) => c.id === childId);
    syncLiveConvenienceKeys(child);
  }
  emit("we-children-changed", { count: list.length });
}

/** Remove a child and clean up their bucketed keys. */
export function removeChild(childId) {
  const before = listChildren();
  const list = before.filter((c) => c.id !== childId);
  saveList(list);
  for (const k of SCOPED_KEYS) safeRm(bucketKey(childId, k));
  if (getActiveChildId() === childId) {
    safeRm(ACTIVE_ID_KEY);
    // Wipe the live keys so no ghost data persists.
    for (const k of SCOPED_KEYS) safeRm(k);
    // Auto-switch to the next child if any remain.
    if (list[0]) switchToChild(list[0].id);
  }
}

/**
 * Switch the active child. Stashes the outgoing child's live state and
 * hydrates the incoming child's. Fires `we-active-child-changed` so
 * the header switcher, tier home, and progress meters re-render.
 */
export function switchToChild(childId) {
  const prev = getActiveChildId();
  if (prev === childId) return;
  if (prev) stashActive(prev);
  safeSet(ACTIVE_ID_KEY, childId);
  hydrateActive(childId);
  const child = listChildren().find((c) => c.id === childId);
  syncLiveConvenienceKeys(child);
  emit("we-active-child-changed", { id: childId });
  emit("we-age-changed", { id: TIER_TO_AGE[child?.tier] || null });
  emit("we-progress-changed", {});
}

/** Read the child's progress snapshot — works for active and non-active. */
export function childStats(childId) {
  const active = getActiveChildId();
  const raw = active === childId
    ? safeGet("we_progress_v1")
    : safeGet(bucketKey(childId, "we_progress_v1"));
  let p = {};
  try { p = JSON.parse(raw || "{}"); } catch { /* ignore */ }
  return {
    stars: p.stars || 0,
    streak: p.streak || 0,
    countries: (p.countriesLearned || []).length,
    badges: (p.badges || []).length,
    coins: p.coins || 0,
    lastPlayed: p.lastPlayed || null,
  };
}

/* ─────────────────────────────────────────────────────────────
 * Persistent per-child access codes ("kid login" from any device)
 * ────────────────────────────────────────────────────────── */
const API = process.env.REACT_APP_BACKEND_URL;

async function apiFetch(path, opts = {}) {
  // 15s timeout so a slow/dead backend never hangs the UI. AbortController
  // is universally supported on the browsers we target (2018+).
  const ctl = new AbortController();
  const timer = setTimeout(() => ctl.abort(), 15000);
  let res;
  try {
    res = await fetch(`${API}${path}`, {
      ...opts,
      signal: ctl.signal,
      headers: { "Content-Type": "application/json", ...(opts.headers || {}) },
    });
  } catch (e) {
    if (e.name === "AbortError") throw new Error("Server took too long — try again");
    throw new Error("Network error — check your connection");
  } finally {
    clearTimeout(timer);
  }
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.detail || `Request failed (${res.status})`);
  return body;
}

function currentProgressSnapshot() {
  try { return JSON.parse(safeGet("we_progress_v1") || "{}"); }
  catch { return {}; }
}

/** Read a child's progress snapshot regardless of whether they are
 *  currently the "playing" child. For the active child we read the
 *  live `we_progress_v1` key; for anyone else we read their bucketed
 *  copy at `we_child__<id>__we_progress_v1`. Returns `{}` when the
 *  child has no progress yet — never `null` — so the server always
 *  gets a well-typed dict. */
function progressForChild(childId) {
  const key = getActiveChildId() === childId
    ? "we_progress_v1"
    : bucketKey(childId, "we_progress_v1");
  try { return JSON.parse(safeGet(key) || "{}"); }
  catch { return {}; }
}

/** Mint (or refresh) the child's permanent 6-char access code and
 *  stash it back onto their profile. Idempotent per child.
 *
 *  Cycle 42 fix: previously we sent `null` for non-active children,
 *  which the server persisted as an empty `{}` snapshot — meaning
 *  ANY child other than the one currently playing would appear
 *  reset (0 stars / 0 streak / no countries) on the redeeming
 *  device. Now we read from the correct bucket so every child's
 *  code carries their real progress. */
export async function mintAccessCode(childId) {
  const child = listChildren().find((c) => c.id === childId);
  if (!child) throw new Error("Child not found");
  const body = await apiFetch("/api/access-code/mint", {
    method: "POST",
    body: JSON.stringify({
      child_id: child.id,
      child_name: child.name,
      tier: child.tier,
      avatar: child.avatar || "🦊",
      birth_country: child.birthCountry || "",
      progress: progressForChild(child.id),
    }),
  });
  updateChild(child.id, {
    accessCode: body.code,
    accessCodeSyncedAt: body.snapshot_updated_at,
  });
  return body.code;
}

/** Push the currently-active child's latest progress up to the server
 *  so the next redeem on another device pulls the freshest state.
 *  Returns null with no error when there is nothing to sync (no
 *  active child or no code). Throws on genuine network failure so
 *  callers can surface it. */
export async function syncActiveProgress() {
  const child = getActiveChild();
  if (!child || !child.accessCode) return null;
  const body = await apiFetch("/api/access-code/sync", {
    method: "POST",
    body: JSON.stringify({ code: child.accessCode, progress: currentProgressSnapshot() }),
  });
  updateChild(child.id, { accessCodeSyncedAt: body.snapshot_updated_at });
  return body.snapshot_updated_at;
}

/** Rotate a child's code — old string is revoked, snapshot preserved. */
export async function regenerateAccessCode(childId) {
  const body = await apiFetch("/api/access-code/regenerate", {
    method: "POST",
    body: JSON.stringify({ child_id: childId }),
  });
  updateChild(childId, { accessCode: body.code });
  return body.code;
}

/** Redeem a code on a fresh device. Fetches the child snapshot +
 *  progress, materialises them as a local child, activates them.
 *  Returns the local child object.
 */
export async function redeemAccessCode(code) {
  const cleaned = String(code || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 6);
  if (cleaned.length !== 6) throw new Error("Code must be 6 characters");
  const body = await apiFetch("/api/access-code/redeem", {
    method: "POST",
    body: JSON.stringify({ code: cleaned }),
  });
  const snap = body.child || {};
  // Is this child already local on THIS device? (Same server-issued id
  // means it's the same profile — refresh rather than duplicate.)
  const existing = listChildren().find((c) => c.id === snap.id);
  let localId;
  if (existing) {
    updateChild(existing.id, {
      name: snap.name,
      tier: snap.tier,
      avatar: snap.avatar,
      birthCountry: snap.birth_country,
      accessCode: cleaned,
    });
    localId = existing.id;
  } else {
    // Create a local child that reuses the SERVER id so subsequent
    // syncs from either device converge on the same document.
    const list = listChildren();
    const child = {
      id: snap.id || uid(),
      createdAt: new Date().toISOString(),
      name: snap.name || "Explorer",
      tier: snap.tier || "explorers",
      avatar: snap.avatar || "🦊",
      birthCountry: snap.birth_country || "",
      accessCode: cleaned,
    };
    list.push(child);
    saveList(list);
    localId = child.id;
  }
  // Stash whatever is currently live under an anonymous bucket so we
  // don't clobber a signed-in user's data, then hydrate the redeemed
  // progress into the live keys.
  const prev = getActiveChildId();
  if (prev && prev !== localId) stashActive(prev);
  safeSet(ACTIVE_ID_KEY, localId);
  // Wipe live scoped keys first — a clean adoption.
  for (const k of SCOPED_KEYS) safeRm(k);
  // Write the incoming progress and profile-derived convenience keys.
  const progress = body.progress || {};
  if (progress && typeof progress === "object" && Object.keys(progress).length) {
    safeSet("we_progress_v1", JSON.stringify(progress));
  }
  const child = listChildren().find((c) => c.id === localId);
  syncLiveConvenienceKeys(child);
  // Also stash the freshly-hydrated live state into the child's bucket.
  stashActive(localId);
  emit("we-active-child-changed", { id: localId });
  emit("we-progress-changed", {});
  return child;
}

/** Fun preset avatars for the child picker. */
export const AVATAR_CHOICES = [
  "🦊", "🐼", "🐨", "🐯", "🦁", "🐸", "🐧", "🦉",
  "🐢", "🦄", "🐙", "🐝", "🦋", "🐬", "🦖", "🦕",
  "🐰", "🐻", "🐺", "🐵",
];

/** Tier presets used across the picker + Family page. */
export const TIER_CHOICES = [
  { id: "seedlings", label: "Seedlings", range: "4-6",   emoji: "🌱", color: "bg-rose-200" },
  { id: "explorers", label: "Explorers", range: "7-10",  emoji: "🌿", color: "bg-yellow-200" },
  { id: "pioneers",  label: "Pioneers",  range: "11-15", emoji: "🌳", color: "bg-emerald-200" },
];

/* ─────────────────────────────────────────────────────────────
 * Auto-sync progress → server (debounced, silent).
 *
 * Runs once per page-load. Whenever `we-progress-changed` fires
 * AND the active child has an access code, wait 3s of quiet and
 * then push the current snapshot up. Errors are swallowed — this
 * is a best-effort background sync, not a critical path.
 * ────────────────────────────────────────────────────────── */
let _autoSyncTimer = null;
let _autoSyncInited = false;

function scheduleAutoSync() {
  if (typeof window === "undefined") return;
  if (_autoSyncTimer) clearTimeout(_autoSyncTimer);
  _autoSyncTimer = setTimeout(() => {
    _autoSyncTimer = null;
    // Only when navigator is online — no pointless retries offline.
    if (typeof navigator !== "undefined" && navigator.onLine === false) return;
    // Fire-and-forget: this is background best-effort, we don't want
    // it to blow up the UI on flaky wifi. syncActiveProgress now
    // throws on real failures for foreground callers to surface — we
    // swallow it here at the edge.
    syncActiveProgress().catch(() => { /* silent bg sync */ });
  }, 3000);
}

export function initChildrenAutoSync() {
  if (_autoSyncInited || typeof window === "undefined") return;
  _autoSyncInited = true;
  window.addEventListener("we-progress-changed", scheduleAutoSync);
  // Also fire on unload as a final flush — best-effort using
  // sendBeacon-ish semantics via a plain fetch keepalive.
  window.addEventListener("beforeunload", () => {
    const child = getActiveChild();
    if (!child?.accessCode) return;
    try {
      fetch(`${API}/api/access-code/sync`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: child.accessCode, progress: currentProgressSnapshot() }),
        keepalive: true,
      });
    } catch { /* swallow */ }
  });
}

// Kick off auto-sync as soon as this module is imported anywhere.
initChildrenAutoSync();
